'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import useEntitlements from '@/hooks/useEntitlements';
import {
  collection,
  query,
  where,
  onSnapshot,
  documentId,
  or,
} from 'firebase/firestore';
import { isTeamMatchReady } from '@/utils/teamUtils';
import { resolveMarketFromZip } from '@/utils/geoUtils'; 

const DEBUG = '[TeamContext]';
const LS_KEY = 'scrimly.activeTeamId';

const TeamContext = createContext(null);

export const useTeam = () => {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeam must be used inside <TeamProvider>');
  return ctx;
};

export default function TeamProvider({ children }) {
  // ✅ 1. Get Loading State from Auth
  const { firebaseUser, userProfile, profileLoading } = useAuth();
  
  // ✅ 2. Get Loading State from Entitlements
  const { isPro, plan, loading: entitlementsLoading } = useEntitlements(firebaseUser?.uid);

  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [teamsError, setTeamsError] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  useEffect(() => {
    setTeams([]);
    setTeamsError(null);
    setLoadingTeams(true);
    setSelectedTeamId(null);

    if (!firebaseUser?.uid) {
      setLoadingTeams(false);
      localStorage.removeItem(LS_KEY);
      return;
    }

    // ✅ 3. SAFETY BLOCK: If profile is loading, STOP. 
    // Do not fetch teams yet. Wait for profile to confirm teamIds.
    if (profileLoading) return;

    const uid = firebaseUser.uid;
    const teamIds = Array.isArray(userProfile?.teamIds)
      ? userProfile.teamIds.filter(Boolean)
      : [];

    let unsubAll = () => {};

    const subscribeByIds = (ids) => {
      if (!ids.length) {
        setTeams([]);
        setLoadingTeams(false);
        return () => {};
      }

      const chunks = [];
      for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

      const unsubs = chunks.map((chunk) =>
        onSnapshot(
          query(collection(db, 'teams'), where(documentId(), 'in', chunk)),
          (snap) => {
            setTeams((prev) => {
              const map = new Map(prev.map((t) => [t.id, t]));
              snap.docs.forEach((d) => {
                const raw = { id: d.id, ...d.data() };
                // Filter archived teams. The Teams page (My Teams) has its
                // own "Show archived" toggle and uses isArchivedFlag on the
                // raw list, but the sidebar / TeamSwitcher / global team
                // picker all read from this context — they should NEVER
                // see archived teams unless we explicitly add a flag to do so.
                const isArchived = raw.isArchived === undefined ? !!raw.archived : !!raw.isArchived;
                if (isArchived) {
                  map.delete(d.id);
                  return;
                }
                const withFlag = {
                  ...raw,
                  matchReady: isTeamMatchReady(raw),
                };
                map.set(d.id, withFlag);
              });
              return Array.from(map.values()).sort((a, b) =>
                (a.name || '').localeCompare(b.name || '')
              );
            });
            setLoadingTeams(false);
          },
          (err) => {
            console.error(`${DEBUG} byIds error`, err);
            setTeamsError(err);
            setLoadingTeams(false);
          }
        )
      );
      return () => unsubs.forEach((u) => u && u());
    };

    const subscribeByMembership = () => {
      const base = collection(db, 'teams');
      const constraints = or(
        where('ownerId', '==', uid),
        where('coachUid', '==', uid),
        where('coachId', '==', uid),
        where('memberUids', 'array-contains', uid),
        where('members', 'array-contains', uid),
        where('managerUids', 'array-contains', uid)
      );

      return onSnapshot(
        query(base, constraints),
        (snap) => {
          const rows = snap.docs
            .map((d) => {
              const raw = { id: d.id, ...d.data() };
              return { ...raw, matchReady: isTeamMatchReady(raw) };
            })
            // Same filter as the byIds path above. Archived teams stay in
            // Firestore but should never appear in the sidebar / picker.
            // The Teams page is the only surface that opts back in via its
            // own "Show archived" toggle.
            .filter((t) => !(t.isArchived === undefined ? !!t.archived : !!t.isArchived))
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setTeams(rows);
          setLoadingTeams(false);
        },
        (err) => {
          console.error(`${DEBUG} membership query error`, err);
          setTeamsError(err);
          setLoadingTeams(false);
        }
      );
    };

    unsubAll = teamIds.length ? subscribeByIds(teamIds) : subscribeByMembership();

    return () => unsubAll && unsubAll();
  }, [firebaseUser?.uid, userProfile?.teamIds, profileLoading]); 

  const teamMap = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);

  const currentTeam = useMemo(() => {
    if (!selectedTeamId) return teams[0] || null;
    return teamMap.get(selectedTeamId) || teams[0] || null;
  }, [selectedTeamId, teamMap, teams]);

  const currentMarket = useMemo(() => {
    if (!currentTeam) return null;
    let zipToTry = currentTeam.zip || currentTeam.zipCode;
    if (!zipToTry && currentTeam.address) {
       const match = currentTeam.address.match(/CA\s+(\d{5})/i) || currentTeam.address.match(/(\d{5})$/);
       if (match) zipToTry = match[1];
    }
    return resolveMarketFromZip(zipToTry);
  }, [currentTeam]);

  const currentTeamMatchReady = useMemo(
    () => isTeamMatchReady(currentTeam),
    [currentTeam]
  );

  const setCurrentTeam = (teamOrId) => {
    const id = typeof teamOrId === 'string' ? teamOrId : teamOrId?.id;
    if (id) {
      setSelectedTeamId(id);
      localStorage.setItem(LS_KEY, id);
    } else {
      setSelectedTeamId(null);
      localStorage.removeItem(LS_KEY);
    }
  };

  useEffect(() => {
    if (loadingTeams || !firebaseUser?.uid) return;
    if (selectedTeamId && teamMap.has(selectedTeamId)) return;

    const persisted = localStorage.getItem(LS_KEY);
    if (persisted && teamMap.has(persisted)) {
      setSelectedTeamId(persisted);
      return;
    }

    if (teams.length) {
      setSelectedTeamId(teams[0].id);
      localStorage.setItem(LS_KEY, teams[0].id);
    }
  }, [loadingTeams, selectedTeamId, teamMap, teams, firebaseUser?.uid]);

  const managedTeams = useMemo(() => {
    const uid = firebaseUser?.uid;
    if (!uid) return [];
    return teams.filter(
      (t) =>
        t.ownerId === uid ||
        t.coachUid === uid ||
        t.coachId === uid ||
        (Array.isArray(t.managerUids) && t.managerUids.includes(uid)) ||
        (Array.isArray(t.members) && t.members.includes(uid))
    );
  }, [teams, firebaseUser?.uid]);

  const canAddTeam = isPro || managedTeams.length < 1;

  // ✅ 4. MASTER LOADING STATE
  // If ANY part of the user data is loading, we tell the app we are loading.
  // This is what `InviteProGuard` will check.
  const isGlobalLoading = 
    loadingTeams || 
    entitlementsLoading || 
    (!!firebaseUser && profileLoading);

  const value = useMemo(
    () => ({
      teams,
      managedTeams,
      teamMap,
      currentTeam,
      currentMarket,
      currentTeamMatchReady,
      selectedTeamId,
      setCurrentTeam,
      setSelectedTeamId,
      // ✅ Export combined loading state
      loadingTeams: isGlobalLoading,
      teamsError,
      isTeamMatchReady,
      isPro,
      plan,
      canAddTeam,
    }),
    [teams, managedTeams, teamMap, currentTeam, currentMarket, currentTeamMatchReady, selectedTeamId, isGlobalLoading, teamsError, isPro, plan, canAddTeam]
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}